import { useContext, useEffect, useState } from "react";
import {
  Box,
  Chip,
  IconButton,
  TableCell,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";
import EnhancedTable from "../../layouts/table";
import PageTitle from "../PageTitle";
import FilterListIcon from "@mui/icons-material/FilterList";
import FilterListOffIcon from "@mui/icons-material/FilterListOff";
import FilterForm from "./FilterForm";
import PreviewUserModal from "./PreviewUserModal";
import VisibilityIcon from "@mui/icons-material/Visibility";
import axios from "axios";
import LoadingTableRows from "../LoadingTableRows";
import HorizontalRuleIcon from "@mui/icons-material/HorizontalRule";
import AccountTreeOutlinedIcon from "@mui/icons-material/AccountTreeOutlined";
import AutorenewIcon from "@mui/icons-material/Autorenew";
import toast from "react-hot-toast";
import { AppContext } from "../../scripts/context/App";

interface IFilter {
  status: boolean;
  data: {
    user_details: string;
    wallet_address: string;
    status: string;
    date_from: string;
    date_to: string;
  };
}
const initFilterData: IFilter = {
  status: true,
  data: {
    user_details: "",
    wallet_address: "",
    date_from: "",
    date_to: "",
    status: "",
  },
};

export interface IPreviewUser {
  status: boolean;
  data: {
    address: {
      city: string;
      street: string;
      country: string;
      zip_code: string;
    };
    avatar: string;
    created_at: string;
    email: string;
    first_name: string;
    id: number;
    identity: string;
    last_name: string;
    mobile: string;
    name: string;
    sponsor_status: string;
    status: string;
    wallet_address: string;
  };
}

const initPreviewUserState: IPreviewUser = {
  status: false,
  data: {
    address: {
      city: "",
      street: "",
      country: "",
      zip_code: "",
    },
    avatar: "",
    created_at: "",
    email: "",
    first_name: "",
    id: 0,
    identity: "",
    last_name: "",
    mobile: "",
    name: "",
    sponsor_status: "",
    status: "",
    wallet_address: "",
  },
};

export default function CustomersManagement() {
  const { user } = useContext(AppContext);
  const [tableData, setTableDatas] = useState({
    page: 1,
    pagesCount: 10,
    rowsPerPage: 5,
    rowsPerPageOptions: [5, 10, 25, 50, 100],
    tableHead: [
      {
        id: "full_name",
        numeric: false,
        disablePadding: false,
        label: "Full Name",
      },
      {
        id: "email",
        numeric: false,
        disablePadding: false,
        label: "Email Address",
      },
      {
        id: "mobile",
        numeric: false,
        disablePadding: false,
        label: "Mobile Number",
      },
      {
        id: "wallet_address",
        numeric: false,
        disablePadding: false,
        label: "Wallet Address",
      },
      {
        id: "is_sponsor",
        numeric: false,
        disablePadding: false,
        label: "Sponsor Status",
      },
      {
        id: "created_at",
        numeric: false,
        disablePadding: false,
        label: "Join At",
      },
      {
        id: "status",
        numeric: false,
        disablePadding: false,
        label: "Account Status",
      },
      {
        id: "actions",
        numeric: false,
        disablePadding: false,
        label: "Actions",
      },
    ],
    tableItems: [],
  });
  const setTableData = (data: any) => setTableDatas({ ...tableData, ...data });
  const [loading, setLoading] = useState(true);

  const [filter, setFilter] = useState<IFilter>(initFilterData);
  const [preview, setPreview] = useState<IPreviewUser>(initPreviewUserState);
  const [order, setOrder] = useState({
    order: "asc",
    orderBy: "name",
  });

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setTableData({
      rowsPerPage: Number(event.target.value),
      page: 1,
    });

    getCustomers({
      name: filter.data.user_details,
      wallet_address: filter.data.wallet_address,
      status: filter.data.status,
      date_from: filter.data.date_from,
      date_to: filter.data.date_to,

      ...(order.order
        ? {
            sortBy: order.order,
            sortOrder: order.orderBy,
          }
        : {}),
      page: 1,
      perPage: Number(event.target.value),
    });
  };

  const handleChangePage = (_: unknown, newPage: number) => {
    getCustomers({
      name: filter.data.user_details,
      wallet_address: filter.data.wallet_address,
      status: filter.data.status,
      date_from: filter.data.date_from,
      date_to: filter.data.date_to,

      ...(order.order
        ? {
            sortBy: order.order,
            sortOrder: order.orderBy,
          }
        : {}),
      page: newPage,
      perPage: tableData.rowsPerPage,
    });

    setTableData({ page: newPage });
  };

  const getCustomers = async (params?: any) => {
    if (!user.permissions.includes("view_customer")) return;

    try {
      const {
        data: { data },
      } = await axios.get("/customers/get-customers", { params });

      setTableData({
        tableItems: data.data,
        pagesCount: data.total,
        rowsPerPage: data.per_page,
        page: data.current_page,
      });
    } catch (error: any) {
      console.log(error);

      toast.error(error.response.data.message || "something went wrong");
    }
  };

  const getInitData = async () => {
    await getCustomers();

    setLoading(false);
  };

  useEffect(() => {
    if (!user.permissions.includes("view_customer")) return;

    getInitData();
  }, []);

  const handleAssignAsSponsor = async (customer_id: string | number) => {
    if (!user.permissions.includes("assign_customer_as_sponsor"))
      return toast.error("You don't have permission");

    try {
      await axios.post("/customers/assign-as-sponsor", { customer_id });

      toast.success("Successfully assigned as sponsor");
      setTableData({
        tableItems: tableData.tableItems.map((item: any) => {
          if (item.id === customer_id) {
            item.sponsor_status = "Activated";
          }
          return item;
        }),
      });
    } catch (error: any) {
      toast.error(
        error.response.data.message || "Something went wrong, please try again"
      );

      console.log(error, " error while assigning user as sponsor");
    }
  };

  const handleUpdateStatus = async (customer_id: string | number) => {
    if (!user.permissions.includes("active_inactive_customer"))
      return toast.error("You don't have permission");

    try {
      await axios.post("/customers/change-status", { customer_id });

      toast.success("Successfully updated status");
      setTableData({
        tableItems: tableData.tableItems.map((item: any) => {
          if (item.id === customer_id) {
            item.status = item.status === "Active" ? "Inactive" : "Active";
          }
          return item;
        }),
      });
    } catch (error: any) {
      toast.error(
        error.response.data.message || "Something went wrong, please try again"
      );

      console.log(error, " error while assigning user as sponsor");
    }
  };

  const STATUS_CHIP: any = {
    Registered: "info",
    Pending: "info",
    Rejected: "error",
    Active: "success",
    Inactive: "error",
  };

  if (!user.permissions.includes("view_customer"))
    return (
      <Box sx={{ p: 3, textAlign: "center" }}>
        <h3>You dont have permissions to view this page</h3>
      </Box>
    );

  return (
    <>
      <PageTitle
        title="Customers Management"
        description="Manage your customers"
      />
      <PreviewUserModal
        data={preview.data}
        handleClose={() => setPreview(initPreviewUserState)}
        update={() =>
          getCustomers({
            name: filter.data.user_details,
            wallet_address: filter.data.wallet_address,
            status: filter.data.status,
            date_from: filter.data.date_from,
            date_to: filter.data.date_to,

            sortBy: order.order,
            sortOrder: order.orderBy,
            page: 1,
            perPage: tableData.rowsPerPage,
          })
        }
      />
      <Box sx={{ p: 3 }}>
        <EnhancedTable
          handleChangeRowsPerPage={handleChangeRowsPerPage}
          handleChangePage={handleChangePage}
          handleRequestSortHeader={(orderFunc, orderByFunc) => {
            if (!orderByFunc) return;

            getCustomers({
              name: filter.data.user_details,
              wallet_address: filter.data.wallet_address,
              status: filter.data.status,
              date_from: filter.data.date_from,
              date_to: filter.data.date_to,

              sortBy: orderByFunc,
              sortOrder: orderFunc,
              page: 1,
              perPage: tableData.rowsPerPage,
            });
            setOrder({
              order: orderByFunc,
              orderBy: orderFunc,
            });
          }}
          minTableHeadWidth="200px"
          TableRows={
            loading ? (
              <LoadingTableRows elementsCount={tableData.tableHead.length} />
            ) : tableData.tableItems.length > 0 ? (
              tableData.tableItems.map((row: any, index: number) => (
                <TableRow key={index}>
                  <TableCell>{row.name}</TableCell>
                  <TableCell>{row.email}</TableCell>
                  <TableCell>{row.mobile}</TableCell>
                  <TableCell>
                    {row.wallet_address || <HorizontalRuleIcon />}
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={row.sponsor_status}
                      variant="outlined"
                      color={
                        row.sponsor_status === "Activated" ? "success" : "error"
                      }
                    />
                  </TableCell>
                  <TableCell>{row.created_at}</TableCell>
                  <TableCell>
                    <Chip
                      label={row.status}
                      variant="outlined"
                      color={STATUS_CHIP[row.status]}
                    />
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: "flex" }}>
                      <Tooltip title="Details">
                        <IconButton
                          disabled={
                            !["Rejected", "Pending"].includes(row.status)
                          }
                          color="primary"
                          onClick={async () => {
                            try {
                              const { data } = await axios.get(
                                "/customers/details",
                                {
                                  params: {
                                    customer_id: row.id,
                                  },
                                }
                              );

                              setPreview({
                                status: true,
                                data: {
                                  ...row,
                                  identity: data.document,
                                  identity_type: data.document_type,
                                },
                              });
                            } catch (error: any) {
                              toast.error(
                                error.response.data.message ||
                                  "something went wrong"
                              );
                            }
                          }}
                        >
                          <VisibilityIcon />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Update Status">
                        <IconButton
                          disabled={
                            !["Active", "Inactive"].includes(row.status)
                          }
                          onClick={() => {
                            if (
                              !user.permissions.includes(
                                "active_inactive_customer"
                              )
                            )
                              return toast.error("You don't have permission");

                            handleUpdateStatus(row.id);
                          }}
                          color="primary"
                        >
                          <AutorenewIcon />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Assign as sponsor">
                        <IconButton
                          disabled={
                            row.sponsor_status === "Activated" ||
                            row.status !== "Active"
                          }
                          onClick={() => {
                            if (
                              !user.permissions.includes(
                                "assign_customer_as_sponsor"
                              )
                            )
                              return toast.error("You don't have permission");

                            handleAssignAsSponsor(row.id);
                          }}
                          color="primary"
                        >
                          <AccountTreeOutlinedIcon />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </TableCell>
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={tableData.tableHead.length}>
                  <Typography
                    variant="body2"
                    color="textPrimary"
                    sx={{ textAlign: "center" }}
                  >
                    No results found
                  </Typography>
                </TableCell>
              </TableRow>
            )
          }
          {...tableData}
        >
          <Box>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <IconButton
                onClick={() =>
                  setFilter({
                    ...filter,
                    status: !filter.status,
                  })
                }
              >
                {filter.status ? <FilterListIcon /> : <FilterListOffIcon />}
              </IconButton>
              <Typography variant="h6">Filter by</Typography>
            </Box>
            {filter.status && (
              <FilterForm
                filter={filter}
                setFilter={setFilter}
                handleSearch={(data) => {
                  if (data?.data) {
                    getCustomers({
                      ...data.data,
                      page: 1,
                      perPage: tableData.rowsPerPage,
                    });
                  } else {
                    getCustomers({
                      ...filter.data,
                      page: 1,
                      perPage: tableData.rowsPerPage,
                    });
                  }
                }}
              />
            )}
          </Box>
        </EnhancedTable>
      </Box>
    </>
  );
}
